import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    MenuItem,
    TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ProspectCreatePayload, SalesFunnelProspect } from '../../salesFunnel.types';

const FACILITY_TYPES = [
    'University',
    'High School',
    'Club',
    'Gym',
    'Pro Team',
    'Academy',
    'Training Center',
    'Other',
];

interface AddProspectDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: ProspectCreatePayload) => Promise<void>;
    prospect?: SalesFunnelProspect | null;
}

const validationSchema = Yup.object({
    prospect_name: Yup.string().required('Prospect name is required').max(255),
    contact_name: Yup.string().nullable().max(255),
    contact_email: Yup.string().nullable().email('Invalid email'),
    contact_phone: Yup.string().nullable().max(50),
    facility_type: Yup.string().nullable().max(100),
    facility_size: Yup.string().nullable().max(100),
    athlete_profile: Yup.string().nullable().max(255),
    athlete_count: Yup.number().nullable().min(0, 'Must be 0 or more').integer(),
    estimated_value: Yup.number().nullable().min(0, 'Must be 0 or more'),
});

const AddProspectDialog: React.FC<AddProspectDialogProps> = ({
    open,
    onClose,
    onSubmit,
    prospect,
}) => {
    const isEdit = !!prospect;

    const formik = useFormik<ProspectCreatePayload>({
        initialValues: {
            prospect_name: prospect?.prospect_name || '',
            contact_name: prospect?.contact_name || '',
            contact_email: prospect?.contact_email || '',
            contact_phone: prospect?.contact_phone || '',
            facility_type: prospect?.facility_type || '',
            facility_size: prospect?.facility_size || '',
            athlete_profile: prospect?.athlete_profile || '',
            athlete_count: prospect?.athlete_count ?? undefined,
            estimated_value: prospect?.pipeline_value ?? undefined,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await onSubmit(values);
                onClose();
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>{isEdit ? 'Edit Prospect' : 'Add New Prospect'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Prospect Name"
                                name="prospect_name"
                                value={formik.values.prospect_name}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.prospect_name &&
                                    Boolean(formik.errors.prospect_name)
                                }
                                helperText={
                                    formik.touched.prospect_name && formik.errors.prospect_name
                                }
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Contact Name"
                                name="contact_name"
                                value={formik.values.contact_name}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Contact Email"
                                name="contact_email"
                                value={formik.values.contact_email}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.contact_email &&
                                    Boolean(formik.errors.contact_email)
                                }
                                helperText={
                                    formik.touched.contact_email && formik.errors.contact_email
                                }
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Contact Phone"
                                name="contact_phone"
                                value={formik.values.contact_phone}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Facility Type"
                                name="facility_type"
                                value={formik.values.facility_type}
                                onChange={formik.handleChange}
                                size="small"
                            >
                                <MenuItem value="">None</MenuItem>
                                {FACILITY_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Facility Size"
                                name="facility_size"
                                value={formik.values.facility_size}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Athlete Count"
                                name="athlete_count"
                                type="number"
                                value={formik.values.athlete_count ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Estimated Deal Value"
                                name="estimated_value"
                                type="number"
                                value={formik.values.estimated_value ?? ''}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.estimated_value &&
                                    Boolean(formik.errors.estimated_value)
                                }
                                helperText={
                                    (formik.touched.estimated_value &&
                                        formik.errors.estimated_value) ||
                                    'Your best estimate — refined at sizing'
                                }
                                size="small"
                                inputProps={{ min: 0, step: 1000 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Athlete Profile"
                                name="athlete_profile"
                                value={formik.values.athlete_profile}
                                onChange={formik.handleChange}
                                placeholder="e.g., D1 Baseball, High School Football"
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                        {isEdit ? 'Save Changes' : 'Add Prospect'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddProspectDialog;
